import { http } from './http';
import { API_BASE_URL, ROUTES } from './config';
import { tokenStore } from './tokenStore';
import type {
  SubscriptionPlan,
  SubscriptionPlanInput,
  TenantSubscription,
  TenantSubscriptionRow,
  SubscriptionDashboard,
  SubscriptionBillingCycle,
  SubscriptionMrrPoint,
  SubscriptionChurnPoint,
  SubscriptionPlanDistribution,
} from '../types/domain';

/** SubscriptionsController client — plan catalog (admin-managed) + tenant subscription self-service. */
export const subscriptionsApi = {
  listPlans(includeInactive = false): Promise<SubscriptionPlan[]> {
    const q = includeInactive ? '?includeInactive=true' : '';
    return http.get(`${ROUTES.subscriptions.plans}${q}`);
  },

  createPlan(body: SubscriptionPlanInput): Promise<{ id: string }> {
    return http.post(ROUTES.subscriptions.plans, body);
  },

  updatePlan(id: string, body: SubscriptionPlanInput): Promise<void> {
    return http.put(`${ROUTES.subscriptions.plans}/${id}`, body);
  },

  deletePlan(id: string): Promise<void> {
    return http.delete(`${ROUTES.subscriptions.plans}/${id}`);
  },

  /** The calling tenant's current subscription, or null if never assigned one. */
  async getMySubscription(): Promise<TenantSubscription | null> {
    try {
      return await http.get(ROUTES.subscriptions.tenant);
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  },

  upgrade(planId: string, billingCycle: SubscriptionBillingCycle): Promise<{ id: string }> {
    return http.post(ROUTES.subscriptions.tenantUpgrade, { planId, billingCycle });
  },

  cancel(): Promise<void> {
    return http.post(ROUTES.subscriptions.tenantCancel, {});
  },

  /** Real Stripe Checkout Session — returns the hosted-page URL to redirect to. */
  createCheckout(planId: string, billingCycle: SubscriptionBillingCycle): Promise<{ url: string }> {
    return http.post(ROUTES.subscriptions.tenantCheckout, { planId, billingCycle });
  },

  /** Real Stripe Customer Portal — returns the hosted-page URL; 409s if the tenant has never checked out via Stripe. */
  createPortalSession(): Promise<{ url: string }> {
    return http.post(ROUTES.subscriptions.tenantPortal, {});
  },

  listAllTenantSubscriptions(): Promise<TenantSubscriptionRow[]> {
    return http.get(ROUTES.subscriptions.allTenants);
  },

  getDashboard(): Promise<SubscriptionDashboard> {
    return http.get(ROUTES.subscriptions.dashboard);
  },

  /** Set or clear a tenant's discount — pass discountCode: null to clear. */
  setDiscount(tenantId: string, discountCode: string | null, discountPercent: number | null): Promise<void> {
    return http.put(ROUTES.subscriptions.tenantDiscount(tenantId), { discountCode, discountPercent });
  },

  /** Manual paper-trail refund flag — no real charge reversal (no payment processor wired to subscriptions). */
  markRefunded(tenantId: string, reason: string): Promise<void> {
    return http.post(ROUTES.subscriptions.tenantRefund(tenantId), { reason });
  },

  /** Replaces this tenant's full feature-override set (on top of whatever its plan already grants). */
  setFeatureOverrides(tenantId: string, features: string[]): Promise<void> {
    return http.put(ROUTES.subscriptions.tenantFeatures(tenantId), { features });
  },

  getMrrTrend(): Promise<SubscriptionMrrPoint[]> {
    return http.get(ROUTES.subscriptions.analyticsMrr);
  },

  getChurnTrend(): Promise<SubscriptionChurnPoint[]> {
    return http.get(ROUTES.subscriptions.analyticsChurn);
  },

  getPlanDistribution(): Promise<SubscriptionPlanDistribution[]> {
    return http.get(ROUTES.subscriptions.analyticsDistribution);
  },

  /** CSV export — bypasses the JSON client so we get the raw Blob with auth (mirrors billingApi.exportCsv). */
  async exportTenantsCsv(): Promise<Blob> {
    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.subscriptions.tenantsExportCsv}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Export failed (${response.status})`);
    return response.blob();
  },

  async exportMrrCsv(): Promise<Blob> {
    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.subscriptions.analyticsMrrExportCsv}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Export failed (${response.status})`);
    return response.blob();
  },
};
